/**
 * Notification Context
 *
 * Manages notification state globally and provides real-time updates
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead
} from '../services/notificationService';
import { supabase } from '../services/supabaseClient';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch notifications
  const fetchNotifications = useCallback(async (limit = 50, offset = 0) => {
    console.log('🔔 [NotificationContext] fetchNotifications called');
    console.log('   User:', user?.id);

    if (!user) {
      console.log('   ❌ No user, skipping fetch');
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('   📡 Fetching notifications for user:', user.id);
      const data = await getNotifications(user.id, limit, offset);
      console.log('   ✅ Notifications fetched:', data.length, 'items');
      console.log('   📋 Data:', data);
      setNotifications(data);

      console.log('   📡 Fetching unread count...');
      const count = await getUnreadCount(user.id);
      console.log('   ✅ Unread count:', count);
      setUnreadCount(count);
    } catch (error) {
      console.error('   ❌ Error fetching notifications:', error);
      console.error('   Error details:', error.message);
      console.error('   Error code:', error.code);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Refresh unread count only
  const refreshUnreadCount = useCallback(async () => {
    if (!user) return;

    try {
      const count = await getUnreadCount(user.id);
      setUnreadCount(count);
    } catch (error) {
      console.error('Error refreshing unread count:', error);
    }
  }, [user]);

  // Mark notification as read
  const markNotificationAsRead = useCallback(async (notificationId) => {
    try {
      await markAsRead(notificationId);

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );

      // Decrement unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllNotificationsAsRead = useCallback(async () => {
    if (!user) return;

    try {
      await markAllAsRead(user.id);

      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [user]);

  // Initial fetch when user logs in
  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
    }
  }, [user, fetchNotifications]);

  // Set up real-time subscription for new notifications
  useEffect(() => {
    if (!user) return;

    console.log('🔔 [Realtime] Setting up notification subscription for user:', user.id);

    const subscription = supabase
      .channel(`notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 [Realtime] ✅ New notification received via Realtime!');
          console.log('   Notification:', payload.new);
          console.log('   Timestamp:', new Date().toISOString());

          // Add new notification to the top of the list
          setNotifications(prev => [payload.new, ...prev]);

          // Increment unread count
          setUnreadCount(prev => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 [Realtime] Notification updated:', payload.new);

          // Update notification in list
          setNotifications(prev =>
            prev.map(n => n.id === payload.new.id ? payload.new : n)
          );

          // Refresh unread count
          refreshUnreadCount();
        }
      )
      .subscribe((status) => {
        console.log('🔔 [Realtime] Subscription status:', status);

        if (status === 'SUBSCRIBED') {
          console.log('   ✅ Successfully subscribed to notifications channel');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('   ❌ Channel error - Realtime may not be working');
        } else if (status === 'TIMED_OUT') {
          console.error('   ⏱️  Subscription timed out');
        } else if (status === 'CLOSED') {
          console.log('   🔕 Subscription closed');
        }
      });

    return () => {
      console.log('🔕 [Realtime] Cleaning up notification subscription');
      subscription.unsubscribe();
    };
  }, [user, refreshUnreadCount]);

  // No polling needed - notifications are event-driven via Realtime
  // When a notification is created, it's immediately pushed via Supabase Realtime

  // Force immediate refresh (for optimistic updates)
  const forceRefresh = useCallback(async () => {
    console.log('🔄 [ForceRefresh] Forcing immediate notification refresh...');
    await Promise.all([
      fetchNotifications(),
      refreshUnreadCount()
    ]);
    console.log('🔄 [ForceRefresh] Refresh complete');
  }, [fetchNotifications, refreshUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    refreshUnreadCount,
    forceRefresh,
    markNotificationAsRead,
    markAllNotificationsAsRead
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
